import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmptyState from '@/components/base/EmptyState';
import MediaCard from '@/components/feature/MediaCard';
import { mediaList } from '@/mocks/media';
import { searchMedia } from '@/utils/media';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { MEDIA_TYPE_LABEL, type MediaType } from '@/types/media';

type TypeFilter = 'all' | MediaType;

const TYPE_TABS: { key: TypeFilter; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
  { key: 'variety', label: '综艺' },
];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = (searchParams.get('q') || '').trim();

  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  useDocumentTitle(
    keyword ? `“${keyword}”的搜索结果 - 蘑菇视频` : '影视搜索 - 蘑菇视频',
    keyword
      ? `蘑菇视频为你找到与“${keyword}”相关的电影、电视剧与综艺内容。`
      : '在蘑菇视频搜索电影、电视剧与综艺，快速找到你想看的内容。',
  );

  const results = useMemo(() => searchMedia(mediaList, keyword), [keyword]);
  const filtered = useMemo(
    () => (typeFilter === 'all' ? results : results.filter((item) => item.type === typeFilter)),
    [results, typeFilter],
  );

  const countByType = (type: TypeFilter) =>
    type === 'all' ? results.length : results.filter((item) => item.type === type).length;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (value) navigate(`/search?q=${encodeURIComponent(value)}`);
    else navigate('/search');
    setTypeFilter('all');
  };

  return (
    <div className="w-full max-w-content mx-auto px-4 md:px-8 py-6">
      <section className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-8">
        <h1 className="text-xl md:text-2xl font-black text-foreground-950 mb-4">影视搜索</h1>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              type="search"
              name="q"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称、类型或主演，例如：科幻、林振宇"
              className="w-full h-12 pl-11 pr-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-search-line text-lg"></i>
            搜索
          </button>
        </form>
      </section>

      {!keyword ? (
        <div className="mt-10">
          <EmptyState
            title="输入关键词开始搜索"
            description="你可以搜索影视名称、类型标签或主演姓名，我们会为你匹配最相关的内容。"
            icon="ri-search-line"
            actionLabel="返回首页"
            actionTo="/"
          />
        </div>
      ) : (
        <section className="mt-8">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
            <p className="text-sm text-foreground-600">
              关键词 <strong className="text-primary-600">“{keyword}”</strong> 共找到{' '}
              <strong className="text-primary-600">{results.length}</strong> 条结果
            </p>
          </div>

          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit mb-6">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setTypeFilter(tab.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  typeFilter === tab.key
                    ? 'bg-background-50 text-primary-600 font-medium shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {tab.label}
                <span className="ml-1 text-xs text-foreground-400">{countByType(tab.key)}</span>
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              title="没有找到相关结果"
              description={`没有与“${keyword}”匹配的${typeFilter === 'all' ? '影视' : MEDIA_TYPE_LABEL[typeFilter]}内容，换个关键词试试吧。`}
              icon="ri-emotion-sad-line"
              actionLabel="返回首页"
              actionTo="/"
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
              {filtered.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}